/**
 * Sound preview utility.
 * Uses native modules on each platform:
 *   - Android: SoundFileManager (plays res/raw files and content:// URIs)
 *   - iOS: SoundPlayer (plays bundled sounds from Sounds folder)
 *
 * Usage:
 *   import { previewSound, stopPreview } from '../utils/soundPlayer';
 *   await previewSound('road_closed');
 *   await stopPreview();
 */
import {NativeModules, Platform} from 'react-native';

const {SoundFileManager, SoundPlayer} = NativeModules;
let webAudio: any = null;

const getWebSoundUrl = (soundIdentifier: string): string | null => {
  // "system_default" has no concrete media file on web.
  if (!soundIdentifier || soundIdentifier === 'system_default') {
    return null;
  }

  // Ignore device/content URIs on web preview.
  if (soundIdentifier.startsWith('content://') || soundIdentifier.startsWith('file://')) {
    return null;
  }

  const hasExtension = /\.[a-z0-9]+$/i.test(soundIdentifier);
  const fileName = hasExtension ? soundIdentifier : `${soundIdentifier}.mp3`;
  return `/sounds/${fileName}`;
};

/**
 * Play a sound preview.
 * @param soundIdentifier - A bundled filename (e.g. "road_closed"), "system_default",
 *                          or a content:// URI (Android only, for future device sounds).
 * @returns true if playback started, or throws on error.
 */
export const previewSound = async (soundIdentifier: string): Promise<boolean> => {
  try {
    if (Platform.OS === 'android') {
      if (!SoundFileManager) {
        console.warn('SoundFileManager native module not available');
        return false;
      }
      return await SoundFileManager.playSound(soundIdentifier);
    } else if (Platform.OS === 'ios') {
      if (!SoundPlayer) {
        console.warn('SoundPlayer native module not available');
        return false;
      }
      return await SoundPlayer.playSound(soundIdentifier);
    } else if (Platform.OS === 'web') {
      const AudioCtor = (globalThis as any)?.Audio;
      if (!AudioCtor) {
        console.warn('Web Audio API not available');
        return false;
      }

      const soundUrl = getWebSoundUrl(soundIdentifier);
      if (!soundUrl) {
        return false;
      }

      // Stop any currently previewing web sound before starting another.
      if (webAudio) {
        try {
          webAudio.pause();
          webAudio.currentTime = 0;
        } catch {
          // Ignore cleanup failures.
        }
      }

      webAudio = new AudioCtor(soundUrl);
      webAudio.onended = () => {
        webAudio = null;
      };
      await webAudio.play();
      return true;
    }
    return false;
  } catch (error: any) {
    console.error('Sound preview error:', error?.message || error);
    throw error;
  }
};

/**
 * Stop any currently playing sound preview.
 */
export const stopPreview = async (): Promise<boolean> => {
  try {
    if (Platform.OS === 'android') {
      if (!SoundFileManager) return false;
      return await SoundFileManager.stopSound();
    } else if (Platform.OS === 'ios') {
      if (!SoundPlayer) return false;
      return await SoundPlayer.stopSound();
    } else if (Platform.OS === 'web') {
      if (!webAudio) return false;
      try {
        webAudio.pause();
        webAudio.currentTime = 0;
        webAudio = null;
        return true;
      } catch {
        webAudio = null;
        return false;
      }
    }
    return false;
  } catch (error: any) {
    console.error('Stop preview error:', error?.message || error);
    // Don't throw on stop - it's a cleanup operation
    return false;
  }
};
